import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveRouteContext } from '../config/chatbotRouteMap';
import { getComponentContext } from '../config/chatbotContextMap';
import { useSelfHelpChat } from '../hooks/useSelfHelpChat';
import { useAgentChat } from '../hooks/useAgentChat';
import { useAuth } from './AuthContext';
import { useConsumerUnit } from './ConsumerUnitContext';
import { apiFetch } from '../services/apiClient';
import { detectIntent } from '../utils/intentDetector';

const ChatbotContext = createContext(null);

export function ChatbotProvider({ children }) {
  const { getToken } = useAuth();
  const { selectedUnitId } = useConsumerUnit();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [componentContext, setComponentContext] = useState(null);
  const [contextError, setContextError] = useState(null);
  // 'auto' = detect on first message, 'kb' = knowledge base RAG, 'agent' = intelligence layer agent
  const [chatMode, setChatMode] = useState('auto');
  const [agentAlertId, setAgentAlertId] = useState(null);
  // Resolved mode tracks the actual mode after intent detection (for session stickiness)
  const resolvedModeRef = useRef(null);
  // Pending first message for auto-send (KPI explain, huddle explain, etc.)
  const pendingFirstMessageRef = useRef(null);

  // Track latest session ID via ref to avoid stale closures in sendMessage
  const sessionIdRef = useRef(null);
  useEffect(() => { sessionIdRef.current = activeSession?.id ?? null; }, [activeSession]);

  // Ref to always call the latest sendMessage (avoids stale closure in auto-send effect)
  const sendMessageRef = useRef(null);

  const kb = useSelfHelpChat({ getToken });
  const agent = useAgentChat({ getToken });

  // Pick the active chat backend based on resolved mode (auto defaults to kb for UI display)
  const effectiveMode = resolvedModeRef.current ?? (chatMode === 'agent' ? 'agent' : 'kb');
  const activeChatHook = effectiveMode === 'agent' ? agent : kb;

  // Derive page context from route
  const pageContext = resolveRouteContext(location.pathname);

  // Build full context for API calls
  const buildContext = useCallback(() => {
    return {
      route: location.pathname,
      domain: pageContext.domain,
      epic: pageContext.epic,
      persona: pageContext.persona,
      ...(componentContext?.componentId ? { componentId: componentContext.componentId } : {}),
    };
  }, [location.pathname, pageContext, componentContext]);

  // Open chat drawer, optionally with component context.
  // aiContext can be:
  //   - a chatbotContextMap key (e.g. "staffing.coverage-status") → mapped to featureId + suggested Qs
  //   - a plain section title (e.g. "Budget Cards") → used as-is for KB search context
  //   - an object { agent: true, alertId, summary } → opens in agent mode with alert context
  //   - an object { agent: true, kpiLabel, kpiValue, ... firstMessage } → opens in agent mode for KPI explain
  const openChat = useCallback((aiContext) => {
    if (aiContext && typeof aiContext === 'object' && aiContext.agent) {
      // Agent mode — intelligence layer chat (alerts / huddle explain / KPI explain)
      setChatMode('agent');
      resolvedModeRef.current = 'agent';
      const isKpi = !!aiContext.kpiLabel;
      setAgentAlertId(aiContext.alertId || null);
      setComponentContext({
        componentId: isKpi
          ? `kpi-${aiContext.kpiDomain}-${aiContext.kpiLabel}`
          : aiContext.alertId
            ? `agent-alert-${aiContext.alertId}`
            : `agent-huddle-${aiContext.domain || 'general'}`,
        suggested: isKpi
          ? [
              `What is driving ${aiContext.kpiLabel} at ${aiContext.kpiValue} right now?`,
              `How does ${aiContext.kpiLabel} compare to last month?`,
              `What actions can improve ${aiContext.kpiLabel}?`,
            ]
          : [
              'What is the root cause of this alert?',
              'What actions should I take?',
              'How does this impact my other metrics?',
            ],
      });
      const title = isKpi
        ? `${aiContext.kpiLabel}: ${aiContext.kpiValue}`
        : (aiContext.summary || 'Agent Chat');
      setActiveSession({ id: null, title, messages: [], context: {} });
      // Store pending first message for auto-send after drawer opens
      pendingFirstMessageRef.current = aiContext.firstMessage || null;
    } else if (aiContext && typeof aiContext === 'string') {
      // SectionHeading info icon — always KB mode
      setChatMode('kb');
      resolvedModeRef.current = 'kb';
      setAgentAlertId(null);
      const mapped = getComponentContext(aiContext);
      if (mapped) {
        setComponentContext({ ...mapped, componentId: aiContext });
      } else {
        setComponentContext({ componentId: aiContext, suggested: [`Tell me about ${aiContext}`] });
      }
    } else {
      // FAB or no context — auto-detect intent on first message
      setChatMode('auto');
      resolvedModeRef.current = null;
      setAgentAlertId(null);
      setComponentContext(null);
    }
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    pendingFirstMessageRef.current = null;
  }, []);

  // Start a new chat — clears local state only (session created lazily on first message)
  const startNewSession = useCallback(() => {
    setChatMode('auto');
    resolvedModeRef.current = null;
    setAgentAlertId(null);
    setActiveSession({ id: null, title: 'New conversation', messages: [], context: buildContext() });
    setContextError(null);
  }, [buildContext]);

  // Create KB session on backend (called lazily from sendMessage)
  const ensureKBSession = useCallback(async () => {
    try {
      const response = await apiFetch('/api/chatbot/sessions', {
        method: 'POST',
        body: JSON.stringify({ context: buildContext() }),
      }, getToken);
      const sessionId = response.sessionId;
      setActiveSession((prev) => ({ ...prev, id: sessionId }));
      return sessionId;
    } catch (err) {
      console.error('Failed to create KB session:', err);
      return null;
    }
  }, [getToken, buildContext]);

  // Create agent session on backend
  const ensureAgentSession = useCallback(async () => {
    try {
      const endpoint = agentAlertId
        ? `/api/chatbot/agent/from-alert/${agentAlertId}`
        : '/api/chatbot/agent/sessions';
      const payload = { context: buildContext(), org_unit_id: selectedUnitId };
      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      }, getToken);
      const sessionId = response.id || response.sessionId;
      setActiveSession((prev) => ({ ...prev, id: sessionId }));
      return sessionId;
    } catch (err) {
      console.error('Failed to create agent session:', err);
      return null;
    }
  }, [getToken, agentAlertId, buildContext, selectedUnitId]);

  // Send a message
  const sendMessage = useCallback(async (text) => {
    setContextError(null);

    // Resolve intent on first message in auto mode
    let mode = resolvedModeRef.current ?? chatMode;
    if (mode === 'auto') {
      mode = detectIntent(text, null);
      resolvedModeRef.current = mode;
      setChatMode(mode);
    } else {
      // Sticky: keep current mode (detectIntent with currentMode returns same)
      mode = detectIntent(text, mode);
    }

    // Add user message to local state immediately (before any API calls)
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setActiveSession((prev) => ({
      ...prev,
      id: prev?.id ?? null,
      messages: [...(prev?.messages ?? []), userMsg],
    }));

    // Use ref to avoid stale closure on activeSession
    let sessionId = sessionIdRef.current;
    if (!sessionId) {
      sessionId = mode === 'agent'
        ? await ensureAgentSession()
        : await ensureKBSession();
      if (!sessionId) {
        setContextError('Unable to connect to NexoNurse Assistant. Please check your connection and try again.');
        return;
      }
    }

    // Send to appropriate backend
    const result = mode === 'agent'
      ? await agent.sendMessage(sessionId, text)
      : await kb.sendMessage(sessionId, text, buildContext());

    if (result?.text) {
      const assistantMsg = {
        role: 'assistant',
        content: result.text,
        timestamp: new Date().toISOString(),
        messageId: result.messageId,
        citations: result.citations ?? [],
      };
      setActiveSession((prev) => ({
        ...prev,
        messages: [...(prev?.messages ?? []), assistantMsg],
      }));
    }
  }, [chatMode, ensureKBSession, ensureAgentSession, agent, kb, buildContext]);
  sendMessageRef.current = sendMessage;

  // Load existing session
  const loadSession = useCallback(async (sessionId) => {
    try {
      const session = await apiFetch(`/api/chatbot/sessions/${sessionId}`, {}, getToken);
      setChatMode('kb');
      setActiveSession({
        id: session.sessionId,
        title: session.title,
        messages: session.messages ?? [],
        context: session.context,
      });
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  }, [getToken]);

  // Delete session
  const deleteSession = useCallback(async (sessionId) => {
    try {
      await apiFetch(`/api/chatbot/sessions/${sessionId}`, { method: 'DELETE' }, getToken);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  }, [getToken, activeSession]);

  // Listen for SectionHeading AI icon clicks (custom DOM event)
  useEffect(() => {
    const handler = (e) => openChat(e.detail);
    window.addEventListener('nexonurse:open-chatbot', handler);
    return () => window.removeEventListener('nexonurse:open-chatbot', handler);
  }, [openChat]);

  // Auto-send pending first message after drawer opens and session is ready
  useEffect(() => {
    if (!isOpen || !pendingFirstMessageRef.current || !activeSession || activeSession.messages?.length) return;
    const msg = pendingFirstMessageRef.current;
    pendingFirstMessageRef.current = null;
    // Use ref to call the latest sendMessage (avoids stale chatMode closure)
    const timer = setTimeout(() => sendMessageRef.current?.(msg), 300);
    return () => clearTimeout(timer);
  }, [isOpen, activeSession]);

  // Fetch sessions list when drawer opens
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const data = await apiFetch('/api/chatbot/sessions', {}, getToken);
        setSessions(data.sessions ?? []);
      } catch {
        // Silently fail — sessions list is non-critical
      }
    })();
  }, [isOpen, getToken]);

  // Submit feedback
  const submitFeedback = useCallback(async (messageId, rating, comment) => {
    try {
      await apiFetch('/api/chatbot/feedback', {
        method: 'POST',
        body: JSON.stringify({
          messageId,
          sessionId: activeSession?.id,
          rating,
          comment,
        }),
      }, getToken);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  }, [getToken, activeSession]);

  const value = {
    isOpen,
    activeSession,
    sessions,
    isStreaming: activeChatHook.isStreaming,
    streamingText: activeChatHook.streamingText,
    streamingCitations: activeChatHook.streamingCitations,
    streamError: activeChatHook.error || contextError,
    pageContext,
    componentContext,
    chatMode,
    openChat,
    closeChat,
    sendMessage,
    cancelStream: activeChatHook.cancelStream,
    startNewSession,
    loadSession,
    deleteSession,
    submitFeedback,
  };

  return <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>;
}

export function useChatbot() {
  const ctx = useContext(ChatbotContext);
  if (!ctx) throw new Error('useChatbot must be used within ChatbotProvider');
  return ctx;
}
